import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, ArrowLeft, Building2, MapPin, Target, Rocket, 
  Upload, X, CheckCircle, Loader2, AlertCircle, Tag 
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { businessApi, errorMessage, getToken } from '../../lib/api';
import { SELECTED_TIER_KEY } from '../Pricing';
import { HUB_RADIUS_MILES } from '../../components/business/hubRules';
import { trackOnboardingStep, trackOnboardingComplete } from '../../utils/analytics';
import imageCompression from 'browser-image-compression';
import { CATEGORY_LIST, getSubcategories } from '../../config/businessCategories';

// One draft per browser tab: a refresh or a back-navigation on step 4 must not lose
// the business name, address and deal text the user just typed (audit row 78).
const DRAFT_KEY = 'tappd_onboarding_draft';
const readDraft = () => {
  try { return JSON.parse(sessionStorage.getItem(DRAFT_KEY) || '{}') || {}; } catch { return {}; }
};

const OnboardingNew = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const draft = readDraft();
  // Step 3 depends on a distance lookup that is not in the draft, so a restored
  // session resumes at step 2 or earlier.
  const [currentStep, setCurrentStep] = useState(Math.min(draft.currentStep || 1, 2));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hubs, setHubs] = useState([]);
  const [calculatingDistance, setCalculatingDistance] = useState(false);

  // Step 1: Business Basics
  const [businessName, setBusinessName] = useState(draft.businessName || '');
  const [businessDescription, setBusinessDescription] = useState(draft.businessDescription || '');
  const [businessCategory, setBusinessCategory] = useState(draft.businessCategory || '');
  const [businessSubcategory, setBusinessSubcategory] = useState(draft.businessSubcategory || '');
  const [bannerImage, setBannerImage] = useState(null);
  const [bannerPreview, setBannerPreview] = useState('');

  // Get subcategories based on selected category
  const availableSubcategories = businessCategory ? getSubcategories(businessCategory) : [];

  // Step 2: Contact & Location
  const [address, setAddress] = useState(draft.address || '');
  const [city, setCity] = useState(draft.city || '');
  const [state, setState] = useState(draft.state || '');
  const [zipCode, setZipCode] = useState(draft.zipCode || '');
  const [phone, setPhone] = useState(draft.phone || '');
  const [website, setWebsite] = useState(draft.website || '');

  // Step 3: Hub Selection
  const [selectedHubs, setSelectedHubs] = useState([]);

  // Step 4: First Deal
  const [dealTitle, setDealTitle] = useState(draft.dealTitle || '');
  const [dealDescription, setDealDescription] = useState(draft.dealDescription || '');

  // Keep the draft in step with what the user has typed (the banner image is left out:
  // a base64 photo does not belong in sessionStorage).
  useEffect(() => {
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify({
        currentStep, businessName, businessDescription, businessCategory, businessSubcategory,
        address, city, state, zipCode, phone, website, dealTitle, dealDescription,
      }));
    } catch { /* private mode / quota */ }
  }, [currentStep, businessName, businessDescription, businessCategory, businessSubcategory,
      address, city, state, zipCode, phone, website, dealTitle, dealDescription]);

  // Auth guard — any signed-in user may complete onboarding; the plan is bought after.
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;

    // A stored credential is the source of truth (either token the portal accepts)
    if (!getToken('business')) {
      navigate('/');
      return;
    }

    // If user is already a verified advertiser with a business, redirect to dashboard
    if (user?.account_type === 'advertiser' && user?.business_id && !user?.pending_advertiser) {
      navigate('/business/dashboard');
    }
  }, [isAuthenticated, authLoading, user, navigate]);

  // Handle banner image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      // Compress image
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1200,
        useWebWorker: true
      };
      
      const compressedFile = await imageCompression(file, options);
      
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerImage(reader.result);
        setBannerPreview(reader.result);
      };
      reader.readAsDataURL(compressedFile);
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('Failed to upload image');
    }
  };

  // Remove banner image
  const removeBannerImage = () => {
    setBannerImage(null);
    setBannerPreview('');
  };

  // Calculate hub distances when moving to step 3
  const calculateHubDistances = async () => {
    setCalculatingDistance(true);
    setError('');
    
    try {
      const fullAddress = `${address}, ${city}, ${state} ${zipCode}`;

      const response = await businessApi.post('/business/calculate-hub-distances', { address: fullAddress });
      
      setHubs(response.data.hubs);
      
      // Show warning if geocoding failed
      if (response.data.geocoding_failed) {
        setError(response.data.message || 'Address lookup unavailable. Please select your hub manually.');
      }
    } catch (error) {
      console.error('Error calculating distances:', error);
      // Try to get hubs anyway as fallback
      try {
        const hubsResponse = await businessApi.get('/locations');
        if (hubsResponse.data && hubsResponse.data.length > 0) {
          const fallbackHubs = hubsResponse.data.map(hub => ({
            hub_id: hub.id,
            name: hub.name,
            city: hub.city || '',
            state: hub.state || '',
            distance_miles: null,
            within_range: true,
            geocoding_failed: true
          }));
          setHubs(fallbackHubs);
          setError('Address lookup temporarily unavailable. Please select your hub manually.');
        } else {
          setError('Failed to load hub locations. Please try again.');
        }
      } catch (fallbackError) {
        setError('Failed to load hub locations. Please try again.');
      }
    } finally {
      setCalculatingDistance(false);
    }
  };

  // Navigation handlers
  const goToStep = async (step) => {
    setError('');
    
    // Validation for each step
    if (step === 2 && currentStep === 1) {
      if (!businessName.trim()) {
        setError('Business name is required');
        return;
      }
      if (!businessCategory) {
        setError('Please select a business category');
        return;
      }
    }
    
    if (step === 3 && currentStep === 2) {
      if (!address.trim() || !city.trim() || !state.trim() || !zipCode.trim() || !phone.trim()) {
        setError('Please fill in all required fields');
        return;
      }
      // Calculate distances before moving to step 3
      await calculateHubDistances();
    }
    
    if (step === 4 && currentStep === 3) {
      if (selectedHubs.length === 0) {
        setError('Please select at least one hub location');
        return;
      }
      const withinRange = selectedHubs.every(hubId => {
        const hub = hubs.find(h => h.hub_id === hubId);
        return hub && hub.within_range;
      });
      if (!withinRange) {
        setError(`You can only select venues within ${HUB_RADIUS_MILES} miles`);
        return;
      }
    }
    
    const stepNames = { 1: 'business_basics', 2: 'contact_location', 3: 'hub_selection', 4: 'first_deal' };
    trackOnboardingStep({ step, stepName: stepNames[step] || `step_${step}` });
    setCurrentStep(step);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError('');
    }
  };

  // Hub selection toggle
  // Phase 1 (ADR-0009 PD-3): Local = 1 venue, Multi = up to 3. Enforced server-side too.
  const selectedTierKey = (localStorage.getItem(SELECTED_TIER_KEY) || 'local') === 'multi' ? 'multi' : 'local';
  const selectedTierName = selectedTierKey === 'multi' ? 'Multi' : 'Local';
  const hubCap = selectedTierKey === 'multi' ? 3 : 1;
  const toggleHub = (hubId) => {
    setSelectedHubs(prev => {
      if (prev.includes(hubId)) return prev.filter(id => id !== hubId);
      if (hubCap === 1) return [hubId]; // single-venue plan: replace the selection
      if (prev.length >= hubCap) {
        setError(`Your ${selectedTierName} plan covers ${hubCap} venues — deselect one first.`);
        return prev;
      }
      setError('');
      return [...prev, hubId];
    });
  };

  // Final submission
  // State for completion screen

  const handleSubmit = async () => {
    if (!dealTitle.trim() || !dealDescription.trim()) {
      setError('Please fill in deal title and description');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        name: businessName,
        description: businessDescription || undefined,
        category: businessCategory || undefined,
        subcategory: businessSubcategory || undefined,
        banner_image: bannerImage || undefined,
        address,
        city,
        state,
        zip_code: zipCode,
        phone,
        website: website || undefined,
        hub_ids: selectedHubs,
        deal_title: dealTitle,
        deal_description: dealDescription
      };

      // Phase 1 (ADR-0009): one onboarding path. The business is created now and the
      // plan is bought on the next screen (Stripe subscription); no free trial.
      const selectedTier = localStorage.getItem(SELECTED_TIER_KEY) || 'local';
      await businessApi.post('/business/onboard', { ...payload, selected_tier: selectedTier });
      trackOnboardingComplete({ isTrial: false });
      try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* private mode */ }
      navigate(`/business/subscription?plan=${selectedTier}`);
    } catch (error) {
      console.error('Error completing onboarding:', error);
      setError(errorMessage(error, 'Failed to complete onboarding'));
    } finally {
      setLoading(false);
    }
  };

  // V5 Loading State - Purple spinner on Cream background
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FEF7ED' }}>
        <Loader2 className="animate-spin" size={48} style={{ color: '#8b5cf6' }} />
      </div>
    );
  }


  // Step titles for display
  const stepTitles = {
    1: { icon: Building2, title: 'Tell us about your business' },
    2: { icon: MapPin, title: 'Where can customers find you?' },
    3: { icon: Target, title: 'Select Your Advertising Locations' },
    4: { icon: Rocket, title: 'Create Your First Deal! 🎁' }
  };

  const CurrentIcon = stepTitles[currentStep].icon;

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: '#FEF7ED' }}>
      <div className="max-w-4xl mx-auto">
        {/* Progress Header */}
        <div className="mb-8">
          <div className="flex flex-col items-center mb-4">
            <img 
              src="/tappd-logo.png" 
              alt="Tappd" 
              className="h-14 md:h-16 mb-2"
            />
          </div>
          
          {/* V5 Code Accent */}
          <p className="text-center font-mono text-xs mb-2" style={{ color: '#8b5cf6' }}>
            // business_onboarding
          </p>
          
          <p 
            className="text-center mb-6 text-[14px]"
            style={{ color: 'rgba(30, 27, 75, 0.7)', fontFamily: "'DM Sans', sans-serif" }}
          >
            Let&apos;s get your business set up in just a few steps
          </p>
          
          {/* V5 Progress Bar */}
          <div className="flex items-center justify-center gap-2 mb-2">
            {[1, 2, 3, 4].map((step) => (
              <React.Fragment key={step}>
                <div
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all"
                  style={
                    step < currentStep 
                      ? { backgroundColor: '#22c55e', color: 'white' }
                      : step === currentStep
                      ? { background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', color: 'white', boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)' }
                      : { backgroundColor: 'rgba(30, 27, 75, 0.1)', color: 'rgba(30, 27, 75, 0.5)' }
                  }
                >
                  {step < currentStep ? <CheckCircle size={18} /> : step}
                </div>
                {step < 4 && (
                  <div
                    className="w-6 sm:w-12 h-1 transition-all"
                    style={{ backgroundColor: step < currentStep ? '#22c55e' : 'rgba(30, 27, 75, 0.1)' }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
          <p className="text-center text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
            Step {currentStep} of 4
          </p>
        </div>

        {/* V5 Error Message */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-4 p-4 flex items-center gap-2"
              style={{ 
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                borderRadius: '12px'
              }}
            >
              <AlertCircle size={20} style={{ color: '#ef4444' }} />
              <p style={{ color: '#ef4444' }}>{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* V5 Step Content Card */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="bg-white p-5 md:p-8"
          style={{ 
            borderRadius: '12px',
            border: '1px solid rgba(30, 27, 75, 0.05)',
            borderLeft: '4px solid #8b5cf6',
            boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
          }}
        >
          {/* Step Header - V5 Design */}
          <div className="flex items-center gap-3 mb-6">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}
            >
              <CurrentIcon size={24} style={{ color: '#8b5cf6' }} />
            </div>
            <h2 
              className="text-[24px]"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, color: '#1e1b4b' }}
            >
              {stepTitles[currentStep].title}
            </h2>
          </div>

          {/* Step 1: Business Basics */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <label htmlFor="ob-business-name" 
                  className="block text-[14px] font-medium mb-2"
                  style={{ color: 'rgba(30, 27, 75, 0.7)' }}
                >
                  Business Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  id="ob-business-name"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="e.g., Joe's Pizza Shop"
                  className="w-full px-4 transition-all"
                  style={{ 
                    height: '48px',
                    border: '2px solid rgba(30, 27, 75, 0.1)',
                    borderRadius: '12px',
                    fontSize: '16px',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#8b5cf6';
                    e.target.style.boxShadow = '0 0 0 4px rgba(139, 92, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div>
                <label htmlFor="ob-business-description" 
                  className="block text-[14px] font-medium mb-2"
                  style={{ color: 'rgba(30, 27, 75, 0.7)' }}
                >
                  Business Description
                </label>
                <textarea
                  id="ob-business-description"
                  value={businessDescription}
                  onChange={(e) => setBusinessDescription(e.target.value)}
                  placeholder="Tell customers what makes your business special..."
                  rows={4}
                  className="w-full p-4 transition-all resize-none"
                  style={{ 
                    border: '2px solid rgba(30, 27, 75, 0.1)',
                    borderRadius: '12px',
                    fontSize: '16px',
                    outline: 'none',
                    minHeight: '120px'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#8b5cf6';
                    e.target.style.boxShadow = '0 0 0 4px rgba(139, 92, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div>
                <label htmlFor="ob-business-category" 
                  className="block text-[14px] font-medium mb-2"
                  style={{ color: 'rgba(30, 27, 75, 0.7)' }}
                >
                  <div className="flex items-center gap-2">
                    <Tag size={16} style={{ color: '#8b5cf6' }} />
                    Business Category <span style={{ color: '#ef4444' }}>*</span>
                  </div>
                </label>
                <select
                  id="ob-business-category"
                  value={businessCategory}
                  onChange={(e) => {
                    setBusinessCategory(e.target.value);
                    setBusinessSubcategory('');
                  }}
                  className="w-full px-4 bg-white transition-all cursor-pointer"
                  style={{ 
                    height: '48px',
                    border: '2px solid rgba(30, 27, 75, 0.1)',
                    borderRadius: '12px',
                    fontSize: '16px',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#8b5cf6';
                    e.target.style.boxShadow = '0 0 0 4px rgba(139, 92, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="">Select a category...</option>
                  {CATEGORY_LIST.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Subcategory dropdown */}
              {businessCategory && availableSubcategories.length > 0 && (
                <div>
                  <label htmlFor="ob-business-subcategory" 
                    className="block text-[14px] font-medium mb-2"
                    style={{ color: 'rgba(30, 27, 75, 0.7)' }}
                  >
                    <div className="flex items-center gap-2">
                      <Tag size={16} style={{ color: '#8b5cf6' }} />
                      Business Type <span style={{ color: 'rgba(30, 27, 75, 0.4)', fontWeight: 400 }}>(Recommended)</span>
                    </div>
                  </label>
                  <select
                    id="ob-business-subcategory"
                    value={businessSubcategory}
                    onChange={(e) => setBusinessSubcategory(e.target.value)}
                    className="w-full px-4 bg-white transition-all cursor-pointer"
                    style={{ 
                      height: '48px',
                      border: '2px solid rgba(30, 27, 75, 0.1)',
                      borderRadius: '12px',
                      fontSize: '16px',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#8b5cf6';
                      e.target.style.boxShadow = '0 0 0 4px rgba(139, 92, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="">Select a specific type...</option>
                    {availableSubcategories.map((sub) => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </select>
                  <p className="text-[12px] mt-1" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                    This helps customers find your business more easily
                  </p>
                </div>
              )}

              <div>
                <label 
                  className="block text-[14px] font-medium mb-2"
                  style={{ color: 'rgba(30, 27, 75, 0.7)' }}
                >
                  Business Banner Image (Optional)
                </label>
                
                {!bannerPreview ? (
                  <label 
                    className="flex flex-col items-center justify-center w-full h-48 cursor-pointer transition-all hover:border-[#8b5cf6]"
                    style={{ 
                      border: '2px dashed rgba(30, 27, 75, 0.2)',
                      borderRadius: '12px'
                    }}
                  >
                    <Upload size={48} style={{ color: 'rgba(30, 27, 75, 0.3)' }} className="mb-2" />
                    <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Click to upload or drag and drop</p>
                    <p className="text-[12px] mt-1" style={{ color: 'rgba(30, 27, 75, 0.4)' }}>PNG, JPG up to 5MB</p>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                ) : (
                  <div className="relative">
                    <img
                      src={bannerPreview}
                      alt="Banner preview"
                      className="w-full h-48 object-cover"
                      style={{ borderRadius: '12px' }}
                    />
                    <button
                      onClick={removeBannerImage}
                      className="absolute top-2 right-2 p-2 text-white transition-all hover:-translate-y-0.5"
                      style={{ 
                        backgroundColor: '#ef4444',
                        borderRadius: '9999px'
                      }}
                    >
                      <X size={20} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Contact & Location */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div>
                <label htmlFor="ob-address" className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                  Street Address <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  id="ob-address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="123 Main Street"
                  className="w-full px-4"
                  style={{ 
                    height: '48px',
                    border: '2px solid rgba(30, 27, 75, 0.1)',
                    borderRadius: '12px',
                    fontSize: '16px',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#8b5cf6';
                    e.target.style.boxShadow = '0 0 0 4px rgba(139, 92, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="ob-city" className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                    City <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="ob-city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Medford"
                    className="w-full px-4"
                    style={{ 
                      height: '48px',
                      border: '2px solid rgba(30, 27, 75, 0.1)',
                      borderRadius: '12px',
                      fontSize: '16px',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#8b5cf6';
                      e.target.style.boxShadow = '0 0 0 4px rgba(139, 92, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div>
                  <label htmlFor="ob-state" className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                    State <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="ob-state"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    placeholder="NJ"
                    maxLength={2}
                    className="w-full px-4 uppercase"
                    style={{ 
                      height: '48px',
                      border: '2px solid rgba(30, 27, 75, 0.1)',
                      borderRadius: '12px',
                      fontSize: '16px',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#8b5cf6';
                      e.target.style.boxShadow = '0 0 0 4px rgba(139, 92, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="ob-zip" className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                    ZIP Code <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="ob-zip"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    placeholder="08055"
                    maxLength={5}
                    className="w-full px-4"
                    style={{ 
                      height: '48px',
                      border: '2px solid rgba(30, 27, 75, 0.1)',
                      borderRadius: '12px',
                      fontSize: '16px',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#8b5cf6';
                      e.target.style.boxShadow = '0 0 0 4px rgba(139, 92, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div>
                  <label htmlFor="ob-phone" className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                    Phone <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="tel"
                    id="ob-phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                    className="w-full px-4"
                    style={{ 
                      height: '48px',
                      border: '2px solid rgba(30, 27, 75, 0.1)',
                      borderRadius: '12px',
                      fontSize: '16px',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#8b5cf6';
                      e.target.style.boxShadow = '0 0 0 4px rgba(139, 92, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="ob-website" className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                  Website (Optional)
                </label>
                <input
                  type="url"
                  id="ob-website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://www.yourbusiness.com"
                  className="w-full px-4"
                  style={{ 
                    height: '48px',
                    border: '2px solid rgba(30, 27, 75, 0.1)',
                    borderRadius: '12px',
                    fontSize: '16px',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#8b5cf6';
                    e.target.style.boxShadow = '0 0 0 4px rgba(139, 92, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>
          )}

          {/* Step 3: Hub Selection */}
          {currentStep === 3 && (
            <div>
              <p className="text-[14px] mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                Your <strong>{selectedTierName}</strong> plan covers <strong>{hubCap} venue{hubCap === 1 ? '' : 's'}</strong>. Pick {hubCap === 1 ? 'the venue' : `up to ${hubCap} venues`} within {HUB_RADIUS_MILES} miles of your business where your deal should appear.
              </p>
              <p className="text-[13px] mb-6" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                {selectedHubs.length} of {hubCap} selected · <a href="/pricing" className="underline">change plan</a>
              </p>

              {calculatingDistance ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="animate-spin mr-2" size={24} style={{ color: '#8b5cf6' }} />
                  <span style={{ color: 'rgba(30, 27, 75, 0.7)' }}>Calculating distances...</span>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {hubs.map((hub) => (
                    <button
                      type="button"
                      key={hub.hub_id}
                      onClick={() => hub.within_range && toggleHub(hub.hub_id)}
                      aria-pressed={selectedHubs.includes(hub.hub_id)}
                      disabled={!hub.within_range}
                      className="p-4 text-left w-full cursor-pointer transition-all"
                      style={{
                        borderRadius: '12px',
                        border: selectedHubs.includes(hub.hub_id) 
                          ? '2px solid #8b5cf6' 
                          : hub.within_range 
                          ? '2px solid rgba(30, 27, 75, 0.1)' 
                          : '2px solid rgba(30, 27, 75, 0.05)',
                        backgroundColor: selectedHubs.includes(hub.hub_id) 
                          ? 'rgba(139, 92, 246, 0.05)' 
                          : hub.within_range 
                          ? 'white' 
                          : 'rgba(30, 27, 75, 0.02)',
                        opacity: hub.within_range ? 1 : 0.6,
                        cursor: hub.within_range ? 'pointer' : 'not-allowed'
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-bold text-[16px]" style={{ color: '#1e1b4b' }}>{hub.name}</h3>
                          <p className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                            {hub.city}, {hub.state}
                          </p>
                        </div>
                        
                        <div 
                          className="px-3 py-1 text-[12px] font-semibold"
                          style={{
                            borderRadius: '9999px',
                            backgroundColor: hub.within_range ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                            color: hub.within_range ? '#22c55e' : '#ef4444'
                          }}
                        >
                          {hub.distance_miles != null ? `${hub.distance_miles} mi` : 'Distance unknown'}
                        </div>
                      </div>

                      {!hub.within_range && (
                        <p className="text-[12px] mt-2" style={{ color: '#ef4444' }}>
                          Too far — must be within {HUB_RADIUS_MILES} miles
                        </p>
                      )}

                      {selectedHubs.includes(hub.hub_id) && (
                        <div className="mt-2 flex items-center gap-1" style={{ color: '#8b5cf6' }}>
                          <CheckCircle size={16} />
                          <span className="text-[13px] font-semibold">Selected</span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 4: First Deal */}
          {currentStep === 4 && (
            <div>
              <p className="text-[14px] mb-6" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                Create an irresistible offer that will attract customers to your business.
              </p>

              <div className="space-y-6">
                <div>
                  <label htmlFor="ob-deal-title" className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                    Deal Title <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="ob-deal-title"
                    value={dealTitle}
                    onChange={(e) => setDealTitle(e.target.value)}
                    placeholder="e.g., 50% Off Happy Hour 🍺"
                    className="w-full px-4"
                    style={{ 
                      height: '48px',
                      border: '2px solid rgba(30, 27, 75, 0.1)',
                      borderRadius: '12px',
                      fontSize: '16px',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#8b5cf6';
                      e.target.style.boxShadow = '0 0 0 4px rgba(139, 92, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div>
                  <label htmlFor="ob-deal-description" className="block text-[14px] font-medium mb-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                    Deal Description <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <textarea
                    id="ob-deal-description"
                    value={dealDescription}
                    onChange={(e) => setDealDescription(e.target.value)}
                    placeholder="Describe your deal in detail. What do customers get? When is it available?"
                    rows={5}
                    className="w-full p-4 resize-none"
                    style={{ 
                      border: '2px solid rgba(30, 27, 75, 0.1)',
                      borderRadius: '12px',
                      fontSize: '16px',
                      outline: 'none',
                      minHeight: '120px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#8b5cf6';
                      e.target.style.boxShadow = '0 0 0 4px rgba(139, 92, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                {/* V5 Tip Box */}
                <div 
                  className="flex items-start gap-3 p-4"
                  style={{ 
                    backgroundColor: 'rgba(139, 92, 246, 0.05)',
                    border: '1px solid rgba(139, 92, 246, 0.1)',
                    borderRadius: '12px'
                  }}
                >
                  <span className="text-xl">💡</span>
                  <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                    <strong style={{ color: '#8b5cf6' }}>Tip:</strong> Great deals are specific, time-sensitive, and offer real value. 
                    Examples: &quot;Buy one pizza, get one 50% off&quot; or &quot;Free appetizer with any entrée&quot;
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* V5 Navigation Buttons */}
          <div 
            className="flex items-center justify-between mt-8 pt-6"
            style={{ borderTop: '1px solid rgba(30, 27, 75, 0.05)' }}
          >
            {/* Back Button - V5 Ghost style */}
            <button
              onClick={handleBack}
              disabled={currentStep === 1 || calculatingDistance}
              className="flex items-center gap-2 px-6 py-3 font-semibold transition-all"
              style={{
                borderRadius: '12px',
                color: currentStep === 1 || calculatingDistance ? 'rgba(30, 27, 75, 0.3)' : 'rgba(30, 27, 75, 0.7)',
                cursor: currentStep === 1 || calculatingDistance ? 'not-allowed' : 'pointer'
              }}
            >
              <ArrowLeft size={20} />
              Back
            </button>

            {currentStep < 4 ? (
              /* Continue Button - V5 Primary Purple */
              <button
                onClick={() => goToStep(currentStep + 1)}
                disabled={calculatingDistance}
                className="flex items-center gap-2 px-8 py-3 font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
                style={{ 
                  background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                  borderRadius: '12px',
                  boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
                }}
              >
                {calculatingDistance ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Finding Nearby Hubs...
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            ) : (
              /* Launch Button - V5 Green for success/completion */
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center gap-2 px-8 py-3 font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ 
                  background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                  borderRadius: '12px',
                  boxShadow: '0 4px 15px rgba(34, 197, 94, 0.4)'
                }}
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Saving...
                  </>
                ) : (
                  <>
                    <ArrowRight size={20} />
                    Continue to payment
                  </>
                )}
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default OnboardingNew;
